// frontend/src/components/Projects/ProjectAnalysisWizard.jsx

import { useState, useEffect } from 'react'
import { useTechnicalCard } from '../../hooks/useTechnicalCard'
import { useProjects } from '../../hooks/useProjects'
import { useWebSocketEvents } from '../../hooks/useWebSocketEvents'
import { useWebSocketContext } from '../../context/WebSocketContext'
import { Button } from '../Common/Button'
import { Card } from '../Common/Card'
import { Loading } from '../Common/Loading'
import { SheetClassifier } from '../TechnicalCard/SheetClassifier'
import { EstimateList } from '../Estimates/EstimateList'
import { SheetGallery } from '../TechnicalCard/SheetGallery'
import {
  CheckCircle,
  AlertCircle,
  FileStack,
  DollarSign,
  RussianRuble,
  Eye,
  Download,
  ChevronRight,
  Zap,
  Info
} from 'lucide-react'
import toast from 'react-hot-toast'

const STEPS = {
  CLASSIFICATION: 'classification',
  ANALYSIS: 'analysis',
  REVIEW: 'review',
  ESTIMATES: 'estimates',
  COMPLETE: 'complete'
}

export const ProjectAnalysisWizard = ({
  project,
  initialStep = STEPS.CLASSIFICATION,
  onStepChange,
  onComplete
}) => {
  const { sheets, loading: sheetsLoading, fetchSheets, classifySheet, analyzeSheet, updateSheet } = useTechnicalCard()
  const { downloadReport } = useProjects()
  const { isConnected } = useWebSocketContext()

  const [currentStep, setCurrentStep] = useState(initialStep)
  const [analyzing, setAnalyzing] = useState(null)
  const [classifying, setClassifying] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0 })

  // Обработчик WebSocket событий
  const handleWebSocketEvents = {
    onSheetUpdate: (data) => {
      console.log('📨 Обновление листа в визарде:', data)

      if (data.card_id === project.id && data.sheet_id) {
        // Обновляем конкретный лист БЕЗ перезагрузки страницы
        updateSheet(data.sheet_id, {
          sheet_type: data.sheet_type,
          status: data.status,
          operations_data: data.operations_data,
          // S3 поля
          image_s3_key: data.image_s3_key,
          annotated_image_s3_key: data.annotated_image_s3_key,
          image_url: data.image_url,
          annotated_image_url: data.annotated_image_url,
        })

        // Если лист был размечен или проанализирован
        if (data.event_type === 'sheet_classified') {
          setClassifying(null)
          toast.success(`Лист ${data.page_number || data.sheet_id} размечен`, {
            id: `classify-${data.sheet_id}` 
          })
        } else if (data.event_type === 'sheet_analyzed') {
          setAnalyzing(null)
          toast.success(`Лист ${data.page_number || data.sheet_id} проанализирован`, { 
            id: `analyze-${data.sheet_id}` 
          })
        }
      }
    },

    onProgressUpdate: (data) => {
      console.log('📨 Обновление прогресса:', data)

      if (data.card_id === project.id) {
        setProgress({
          current: data.current,
          total: data.total,
          percent: data.progress_percent
        })
      }
    },

    onTechnicalCardUpdate: (data) => {
      console.log('📨 Обновление технической карты:', data)

      if (data.card_id === project.id) {
        // Обработка событий карты (например, завершение обработки)
        if (data.event_type === 'card_status_changed') {
          toast.info(data.message || 'Статус карты обновлен', {
            id: `card-${data.card_id}`
          })
        }
      }
    }
  }

  useWebSocketEvents(handleWebSocketEvents)

  useEffect(() => {
    loadSheets()
  }, [project.id])

  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep)
    }
  }, [currentStep, onStepChange])

  const loadSheets = async () => {
    try {
      await fetchSheets(project.id)
    } catch (error) {
      toast.error('Ошибка при загрузке листов')
      console.error(error)
    }
  }

  const handleClassify = async (sheetId, sheetType) => {
    setClassifying(sheetId)
    try {
      await classifySheet(sheetId, sheetType)
      toast.success('Лист размечен')
//       await loadSheets()
    } catch (error) {
      toast.error('Ошибка при разметки листа')
      console.error(error)
    } finally {
      setClassifying(null)
    }
  }

  const handleAnalyze = async (sheetId) => {
    setAnalyzing(sheetId)
    try {
      await analyzeSheet(sheetId)
      toast.success('Анализ листа завершен')
//       await loadSheets()
    } catch (error) {
      toast.error('Ошибка при анализе листа')
      console.error(error)
    } finally {
      setAnalyzing(null)
    }
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      await downloadReport(project.id)
      toast.success('Отчет успешно скачан')
    } catch (error) {
      toast.error('Ошибка при скачивании отчета')
      console.error(error)
    } finally {
      setIsDownloading(false)
    }
  }

  if (sheetsLoading) {
    return <Loading message="Загрузка листов..." />
  }

  // Расчет статистики
  const stats = {
    total: sheets?.length || 0,
    classified: sheets?.filter(s => s && s.sheet_type && s.sheet_type !== 'unknown').length || 0,
    analyzed: sheets?.filter(s => s && s.status === 'analyzed').length || 0
  }

  // Разрешить переход на следующие этапы, даже если не все листы обработаны
  const canProceedToAnalysis = stats.total > 0
  const canProceedToReview = stats.total > 0
  const canProceedToEstimates = stats.total > 0
  const canProceedToComplete = stats.total > 0

  return (
    <div className="space-y-6">
      {!isConnected && (
        <Card className="bg-yellow-50 border border-yellow-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="text-yellow-600" size={20} />
            <span className="text-sm text-yellow-700">
              Отключено от сервера. Обновления могут быть задержаны.
            </span>
          </div>
        </Card>
      )}

      {/* Визуальный прогресс */}
      <StepIndicator currentStep={currentStep} />

      {/* Шаг 1: Разметка */}
      {currentStep === STEPS.CLASSIFICATION && (
        <ClassificationStep
          sheets={sheets}
          stats={stats}
          onClassify={handleClassify}
          classifying={classifying}
          onNext={() => setCurrentStep(STEPS.ANALYSIS)}
          canProceed={canProceedToAnalysis}
        />
      )}

      {/* Шаг 2: Анализ */}
      {currentStep === STEPS.ANALYSIS && (
        <AnalysisStep
          sheets={sheets}
          stats={stats}
          onAnalyze={handleAnalyze}
          analyzing={analyzing}
          onNext={() => setCurrentStep(STEPS.REVIEW)}
          onBack={() => setCurrentStep(STEPS.CLASSIFICATION)}
          canProceed={canProceedToReview}
        />
      )}

      {/* Шаг 3: Просмотр */}
      {currentStep === STEPS.REVIEW && (
        <ReviewStep
          sheets={sheets}
          onNext={() => setCurrentStep(STEPS.ESTIMATES)}
          onBack={() => setCurrentStep(STEPS.ANALYSIS)}
        />
      )}

      {/* Шаг 4: Сметы */}
      {currentStep === STEPS.ESTIMATES && (
        <EstimatesStep
          project={project}
          onNext={() => setCurrentStep(STEPS.COMPLETE)}
          onBack={() => setCurrentStep(STEPS.REVIEW)}
          canProceed={canProceedToEstimates}
        />
      )}

      {/* Шаг 5: Завершение */}
      {currentStep === STEPS.COMPLETE && (
        <CompleteStep
          project={project}
          stats={stats}
          onDownload={handleDownload}
          isDownloading={isDownloading}
          onClose={onComplete}
        />
      )}
    </div>
  )
}

// Компонент индикатора прогресса
function StepIndicator({ currentStep }) {
  const steps = [
    { id: STEPS.CLASSIFICATION, label: 'Разметка', icon: FileStack },
    { id: STEPS.ANALYSIS, label: 'Анализ', icon: Zap },
    { id: STEPS.REVIEW, label: 'Просмотр', icon: Eye },
    { id: STEPS.ESTIMATES, label: 'Сметы', icon: DollarSign },
    { id: STEPS.COMPLETE, label: 'Завершено', icon: CheckCircle }
  ]

  const currentIndex = steps.findIndex(s => s.id === currentStep)

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const Icon = step.icon
          const isActive = index === currentIndex
          const isCompleted = index < currentIndex
          const isUpcoming = index > currentIndex

          return (
            <div key={step.id} className="flex items-center flex-1">
              {/* Шаг */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg scale-110'
                      : isCompleted
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle size={24} />
                  ) : (
                    <Icon size={24} />
                  )}
                </div>
                <p
                  className={`mt-2 text-sm font-medium text-center whitespace-nowrap ${
                    isActive
                      ? 'text-blue-600'
                      : isCompleted
                      ? 'text-green-600'
                      : 'text-gray-600'
                  }`}
                >
                  {step.label}
                </p>
              </div>

              {/* Линия между шагами */}
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 rounded transition-all duration-200 ${
                    isCompleted ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// Шаг 1: Разметка
function ClassificationStep({ sheets, stats, onClassify, classifying, onNext, canProceed }) {
  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900">Шаг 1: Разметка листов</h3>
            <p className="text-sm text-blue-700 mt-1">
              Определите тип каждого листа: Материалы, Сборка или Операции. Это необходимо для корректного анализа.
            </p>
          </div>
        </div>
      </Card>

      {/* Кнопки навигации */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          disabled={!canProceed}
          className="flex items-center gap-2"
          title={!canProceed ? 'Загрузите листы для продолжения' : ''}
        >
          Далее: Анализ
          <ChevronRight size={20} />
        </Button>
      </div>

      {/* Информационное сообщение о возможности перехода */}
      <Card className="bg-amber-50 border border-amber-200">
        <div className="flex items-start gap-3">
          <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-amber-700">
              Вы можете переходить на следующие этапы, даже если размечена только часть листов. Рекомендуется разметить все листы для лучшего результата.
            </p>
          </div>
        </div>
      </Card>

      {/* Прогресс разметки */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="text-center border-l-4 border-l-yellow-500">
          <p className="text-gray-600 text-sm">Всего листов</p>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </Card>
        <Card className="text-center border-l-4 border-l-blue-500">
          <p className="text-gray-600 text-sm">Размечено</p>
          <p className="text-3xl font-bold text-blue-600">{stats.classified}</p>
        </Card>
        <Card className="text-center border-l-4 border-l-green-500">
          <p className="text-gray-600 text-sm">Прогресс</p>
          <p className="text-3xl font-bold text-green-600">
            {stats.total > 0 ? Math.round((stats.classified / stats.total) * 100) : 0}%
          </p>
        </Card>
      </div>

      {/* Прогресс-бар */}
      {stats.total > 0 && (
        <div className="space-y-2">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-full transition-all duration-300"
              style={{ width: `${(stats.classified / stats.total) * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-600 text-right">
            {stats.classified} из {stats.total} листов размечено
          </p>
        </div>
      )}

      {/* Листы для разметки */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Листы для разметки</h3>
        {sheets && sheets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sheets.map((sheet) => (
              <SheetClassifier
                key={sheet.id}
                sheet={sheet}
                onClassify={onClassify}
                loading={classifying === sheet.id}
                hideAnalyzeButton={true}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileStack size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Листы не найдены</p>
          </div>
        )}
      </div>

    </div>
  )
}

// Шаг 2: Анализ
function AnalysisStep({ sheets, stats, onAnalyze, analyzing, onNext, onBack, canProceed }) {
  const analyzedCount = sheets?.filter(s => s && s.status === 'analyzed').length || 0

  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <Zap className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900">Шаг 2: Анализ листов</h3>
            <p className="text-sm text-blue-700 mt-1">
              Запустите анализ для каждого листа. Система извлечет данные о материалах, операциях и сборке.
            </p>
          </div>
        </div>
      </Card>
      {/* Информационное сообщение о возможности перехода */}
      <Card className="bg-amber-50 border border-amber-200">
        <div className="flex items-start gap-3">
          <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-amber-700">
              Вы можете переходить на следующие этапы, даже если проанализирована только часть листов. Рекомендуется проанализировать все листы для полноты данных.
            </p>
          </div>
        </div>
      </Card>

      {/* Кнопки навигации */}
      <div className="flex justify-between gap-3 pt-6 border-t">
        <Button
          variant="secondary"
          size="lg"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          Назад
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          disabled={!canProceed}
          className="flex items-center gap-2"
          title={!canProceed ? 'Загрузите листы для продолжения' : ''}
        >
          Далее: Просмотр
          <ChevronRight size={20} />
        </Button>
      </div>

      {/* Прогресс анализа */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="text-center border-l-4 border-l-yellow-500">
          <p className="text-gray-600 text-sm">Всего листов</p>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </Card>
        <Card className="text-center border-l-4 border-l-purple-500">
          <p className="text-gray-600 text-sm">Анализировано</p>
          <p className="text-3xl font-bold text-purple-600">{analyzedCount}</p>
        </Card>
        <Card className="text-center border-l-4 border-l-green-500">
          <p className="text-gray-600 text-sm">Прогресс</p>
          <p className="text-3xl font-bold text-green-600">
            {stats.total > 0 ? Math.round((analyzedCount / stats.total) * 100) : 0}%
          </p>
        </Card>
      </div>

      {/* Прогресс-бар */}
      {stats.total > 0 && (
        <div className="space-y-2">
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-purple-500 to-purple-600 h-full transition-all duration-300"
              style={{ width: `${(analyzedCount / stats.total) * 100}%` }}
            />
          </div>
          <p className="text-xs text-gray-600 text-right">
            {analyzedCount} из {stats.total} листов проанализировано
          </p>
        </div>
      )}

      {/* Листы для анализа */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Листы для анализа</h3>
        {sheets && sheets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sheets.map((sheet) => (
              <SheetAnalyzer
                key={sheet ? sheet.id : 0}
                sheet={sheet}
                onAnalyze={onAnalyze}
                loading={analyzing === sheet.id}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileStack size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Листы не найдены</p>
          </div>
        )}
      </div>
    </div>
  )
}

// Компонент для анализа листа
function SheetAnalyzer({ sheet, onAnalyze, loading }) {
  const getSheetTypeLabel = (type) => {
    const labels = {
      'materials': 'Материалы',
      'assembly': 'Сборка',
      'operational': 'Операции',
      'unknown': 'Неизвестно'
    }
    return labels[type] || type
  }

  const getSheetTypeColor = (type) => {
    const colors = {
      'materials': 'bg-blue-100 text-blue-800',
      'assembly': 'bg-green-100 text-green-800',
      'operational': 'bg-purple-100 text-purple-800',
      'unknown': 'bg-gray-100 text-gray-800'
    }
    return colors[type] || colors['unknown']
  }

  const isAnalyzed = sheet.status === 'analyzed'

  return (
    <Card className={`flex flex-col h-full ${isAnalyzed ? 'border-l-4 border-l-green-500' : ''}`}>
      {/* Превью */}
      <div className="aspect-video bg-gray-200 flex items-center justify-center rounded-lg overflow-hidden mb-4">
        {sheet.image_url || sheet.id ? (
          <img
            src={sheet.image_url || `/api/technical_cards/${sheet.id}/image`}
            alt={`Page ${sheet.page_number}`}
            crossOrigin="anonymous"
            loading="lazy"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+'
            }}
            className="w-full h-full object-contain"
          />
        ) : (
          <span className="text-gray-600">Страница {sheet.page_number}</span>
        )}
      </div>

      {/* Информация */}
      <div className="flex-1 mb-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-600">Страница {sheet.page_number}</p>
          <span className={`px-2 py-1 rounded text-xs font-medium ${getSheetTypeColor(sheet.sheet_type)}`}>
            {getSheetTypeLabel(sheet.sheet_type)}
          </span>
        </div>

        {/* Статус анализа */}
        <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50">
          {isAnalyzed ? (
            <>
              <CheckCircle size={18} className="text-green-600" />
              <span className="text-sm font-medium text-green-700">Анализ завершен</span>
            </>
          ) : (
            <>
              <AlertCircle size={18} className="text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700">Ожидает анализа</span>
            </>
          )}
        </div>
      </div>

      {/* Кнопка анализа */}
      <Button
        variant={isAnalyzed ? 'secondary' : 'primary'}
        onClick={() => onAnalyze(sheet.id)}
        disabled={loading || isAnalyzed}
        className="w-full flex items-center justify-center gap-2"
      >
        <Zap size={16} />
        {loading ? 'Анализ...' : isAnalyzed ? 'Проанализирован' : 'Анализировать'}
      </Button>
    </Card>
  )
}

// Шаг 3: Просмотр
function ReviewStep({ sheets, onNext, onBack }) {
  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <Eye className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900">Шаг 3: Просмотр результатов</h3>
            <p className="text-sm text-blue-700 mt-1">
              Проверьте результаты анализа всех листов перед генерацией смет.
            </p>
          </div>
        </div>
      </Card>

      {/* Кнопки навигации */}
      <div className="flex justify-between gap-3 pt-6 border-t">
        <Button
          variant="secondary"
          size="lg"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          Назад
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          className="flex items-center gap-2"
        >
          Далее: Сметы
          <ChevronRight size={20} />
        </Button>
      </div>

      {/* Галерея листов */}
      {sheets && sheets.length > 0 ? (
        <SheetGallery sheets={sheets} />
      ) : (
        <div className="text-center py-12">
          <FileStack size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">Листы не найдены</p>
        </div>
      )}
    </div>
  )
}

// Шаг 4: Сметы
function EstimatesStep({ project, onNext, onBack, canProceed }) {
  return (
    <div className="space-y-6">
      <Card className="bg-blue-50 border border-blue-200">
        <div className="flex items-start gap-3">
          <RussianRuble className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900">Шаг 4: Сметы проекта</h3>
            <p className="text-sm text-blue-700 mt-1">
              Здесь отображаются автоматически рассчитанные сметы на основе анализа листов.
            </p>
          </div>
        </div>
      </Card>

      {/* Кнопки навигации */}
      <div className="flex justify-between gap-3 pt-6 border-t">
        <Button
          variant="secondary"
          size="lg"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          Назад
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onNext}
          disabled={!canProceed}
          className="flex items-center gap-2"
          title={!canProceed ? 'Сметы будут доступны после анализа' : ''}
        >
          Завершить
          <ChevronRight size={20} />
        </Button>
      </div>

      {/* Информация о статусе */}
      {!canProceed && (
        <Card className="bg-yellow-50 border border-yellow-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm text-yellow-700">
                Сметы будут доступны после завершения анализа всех листов. Статус проекта: <strong>{project.status}</strong>
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Сметы: панель расчёта с регионом внутри EstimateList */}
      {canProceed ? (
        <EstimateList estimates={project.estimates || []} cardId={project.id} />
      ) : (
        <Card>
          <div className="text-center py-12">
            <RussianRuble size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              Сметы будут доступны после анализа листов
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}

// Шаг 5: Завершение
function CompleteStep({ project, stats, onDownload, isDownloading, onClose }) {
  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
        <div className="flex items-start gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="font-semibold text-green-900 text-lg">Анализ завершен!</h3>
            <p className="text-sm text-green-700 mt-1">
              Все листы успешно обработаны и размечены. Сметы готовы к использованию.
            </p>
          </div>
        </div>
      </Card>

      {/* Кнопки действий */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
        <Button
          variant="secondary"
          size="lg"
          onClick={onClose}
          className="flex-1 flex items-center justify-center gap-2"
        >
          Закрыть
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={onDownload}
          disabled={isDownloading}
          className="flex-1 flex items-center justify-center gap-2"
        >
          <Download size={20} />
          {isDownloading ? 'Скачивание...' : 'Скачать отчет'}
        </Button>
      </div>

      {/* Итоговая статистика */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="text-center border-l-4 border-l-blue-500">
          <p className="text-gray-600 text-sm">Всего листов</p>
          <p className="text-4xl font-bold text-blue-600">{stats.total}</p>
        </Card>
        <Card className="text-center border-l-4 border-l-green-500">
          <p className="text-gray-600 text-sm">Размечено</p>
          <p className="text-4xl font-bold text-green-600">{stats.classified}</p>
        </Card>
        <Card className="text-center border-l-4 border-l-purple-500">
          <p className="text-gray-600 text-sm">Анализировано</p>
          <p className="text-4xl font-bold text-purple-600">{stats.analyzed}</p>
        </Card>
      </div>

      {/* Информация о проекте */}
      <Card>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Информация о проекте</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-600">Название файла</span>
            <span className="font-semibold text-gray-900">{project.file_name}</span>
          </div>
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-600">Статус</span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              {project.status === 'completed' ? 'Завершено' : project.status}
            </span>
          </div>
          {project.total_cost && (
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Общая стоимость</span>
              <span className="font-semibold text-green-600">
                {project.total_cost.toLocaleString('ru-RU', {
                  style: 'currency',
                  currency: 'RUB',
                  minimumFractionDigits: 0
                })}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-600">Сметы</span>
            <span className="font-semibold text-gray-900">{project.estimates?.length || 0}</span>
          </div>
        </div>
      </Card>

      {/* Следующие действия */}
      <Card className="bg-indigo-50 border border-indigo-200">
        <h3 className="font-semibold text-indigo-900 mb-3">Что дальше?</h3>
        <ul className="space-y-2 text-sm text-indigo-700">
          <li className="flex items-start gap-2">
            <span className="font-bold">1.</span>
            <span>Скачайте отчет со всеми результатами анализа</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">2.</span>
            <span>Используйте сметы для планирования и бюджетирования</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">3.</span>
            <span>Вернитесь к просмотру листов в любое время</span>
          </li>
        </ul>
      </Card>
    </div>
  )
}

export default ProjectAnalysisWizard
